"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import type { Service } from "@/types";
import { getProxiedImageUrl } from "@/lib/utils/image-proxy";
import { getPublicMediaUrl } from "@/lib/utils/media-url";
import { cn } from "@/lib/utils";
import { getCtaDataAttrs } from "@/lib/analytics/cta-attrs";
import { ga4SelectDesign } from "@/lib/analytics/ga4-ecommerce";
import { ArrowButtonLeft, ArrowButtonRight, mediaCarouselArrowGlass } from "@/components/carousels";

export type ServiceDesignVariant = Pick<
	Service,
	| "_id"
	| "id"
	| "title"
	| "slug"
	| "thumbnailImage"
	| "thumbnailDisplayUrl"
	| "media"
	| "detailsHtml"
	| "envatoTemplateLicenseAmounts"
	| "demoUrl"
>;

function firstImageUrl(variant: ServiceDesignVariant): string {
	const firstImage = (variant.media ?? []).find((item) => {
		if (!item || item.type?.toUpperCase() === "VIDEO") return false;
		return Boolean(item.displayUrl?.trim() || item.url?.trim());
	});
	const raw =
		firstImage?.displayUrl?.trim() ||
		firstImage?.url?.trim() ||
		variant.thumbnailDisplayUrl?.trim() ||
		variant.thumbnailImage?.trim() ||
		"";
	const publicUrl = getPublicMediaUrl(raw) ?? raw;
	return getProxiedImageUrl(publicUrl) ?? publicUrl;
}

type Props = {
	landingService: Service;
	variants: ServiceDesignVariant[];
	/** null = landing service’s own design */
	selectedDesignId: string | null;
	onSelect: (designId: string | null) => void;
};

/**
 * In-place design picker for ad landings. Does not navigate; parent swaps media/license/details/FBT.
 */
export default function ServiceDesignPicker({
	landingService,
	variants,
	selectedDesignId,
	onSelect,
}: Props) {
	const scrollerRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
	const thumbRef = useRef<HTMLSpanElement>(null);
	/** Skip mount — `scrollIntoView` was pulling the whole page down to this section. */
	const hasMountedSelectionEffectRef = useRef(false);
	const [canLeft, setCanLeft] = useState(false);
	const [canRight, setCanRight] = useState(false);
	const [arrowTop, setArrowTop] = useState<number | null>(null);

	const landingThumb: ServiceDesignVariant = {
		_id: landingService._id,
		id: landingService.id,
		title: landingService.title,
		slug: landingService.slug,
		thumbnailImage: landingService.thumbnailImage,
		thumbnailDisplayUrl: landingService.thumbnailDisplayUrl,
		media: landingService.media,
		detailsHtml: landingService.detailsHtml,
		envatoTemplateLicenseAmounts: landingService.envatoTemplateLicenseAmounts,
		demoUrl: landingService.demoUrl,
	};

	const options: Array<{ id: string | null; variant: ServiceDesignVariant; designNumber: number }> = [
		{ id: null, variant: landingThumb, designNumber: 1 },
		...variants.map((variant, index) => ({
			id: variant._id,
			variant,
			designNumber: index + 2,
		})),
	];

	const updateArrows = useCallback(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const bounds = scroller.getBoundingClientRect();
		const first = itemRefs.current.get("__landing__");
		const firstRect = first?.getBoundingClientRect();
		const left = Boolean(firstRect && firstRect.left < bounds.left - 1);

		const lastVariant = variants[variants.length - 1];
		const lastKey = lastVariant?._id ?? "__landing__";
		const last = itemRefs.current.get(lastKey);
		const lastRect = last?.getBoundingClientRect();
		const right = Boolean(lastRect && lastRect.right > bounds.right + 1);

		setCanLeft((prev) => (prev === left ? prev : left));
		setCanRight((prev) => (prev === right ? prev : right));

		const thumb = thumbRef.current;
		if (thumb) {
			const top =
				thumb.getBoundingClientRect().top - bounds.top + thumb.getBoundingClientRect().height / 2;
			setArrowTop((prev) => (prev != null && Math.abs(prev - top) < 0.5 ? prev : top));
		}
	}, [variants]);

	const scrollByCard = useCallback((direction: -1 | 1) => {
		const scroller = scrollerRef.current;
		if (!scroller) return;
		const card = itemRefs.current.get("__landing__");
		const step = (card?.offsetWidth ?? 240) + 12;
		scroller.scrollBy({ left: direction * step, behavior: "smooth" });
	}, []);

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;
		scroller.scrollLeft = 0;
		const frame = requestAnimationFrame(updateArrows);
		scroller.addEventListener("scroll", updateArrows, { passive: true });
		const observer = new ResizeObserver(updateArrows);
		observer.observe(scroller);
		return () => {
			cancelAnimationFrame(frame);
			scroller.removeEventListener("scroll", updateArrows);
			observer.disconnect();
		};
	}, [variants.length, updateArrows]);

	/** After a pick, slide that design to the left edge so earlier ones sit off the left wall. */
	useEffect(() => {
		if (variants.length === 0) return;
		if (!hasMountedSelectionEffectRef.current) {
			hasMountedSelectionEffectRef.current = true;
			return;
		}

		const key = selectedDesignId ?? "__landing__";
		const el = itemRefs.current.get(key);
		const scroller = scrollerRef.current;
		if (!el || !scroller) return;

		const overflows = scroller.scrollWidth > scroller.clientWidth + 2;
		if (!overflows) return;

		scroller.scrollTo({
			left: Math.max(0, el.offsetLeft),
			behavior: "smooth",
		});
	}, [selectedDesignId, variants.length]);

	if (variants.length === 0) return null;

	return (
		<section
			className="w-full scroll-mt-[7.5rem] px-[15px] pb-2 pt-4 sm:px-8 lg:px-0"
			aria-label="Choose your design"
		>
			<h2 className="text-center text-lg font-semibold tracking-wide text-foreground sm:text-xl">
				Choose your design
			</h2>
			<p className="mx-auto mt-1 max-w-2xl text-center text-sm leading-snug text-grey-foreground dark:text-muted-foreground">
				Select the design you want for your website to draw styling inspiration from. We can customize
				it to suit your preferences and needs or make custom changes later.
			</p>
			<div
				className="relative mt-4"
				style={{ ["--design-arrow-top" as string]: arrowTop != null ? `${arrowTop}px` : "5.5rem" }}
			>
				{canLeft ? (
					<ArrowButtonLeft
						aria-label="Previous designs"
						disabled={false}
						onClick={() => scrollByCard(-1)}
						className={cn(
							"absolute left-1 top-[var(--design-arrow-top)] z-10 flex size-10 -translate-y-1/2 items-center justify-center sm:left-2 sm:size-11",
							mediaCarouselArrowGlass,
							"[&>svg]:size-5 sm:[&>svg]:size-6",
						)}
					/>
				) : null}
				{canRight ? (
					<ArrowButtonRight
						aria-label="Next designs"
						disabled={false}
						onClick={() => scrollByCard(1)}
						className={cn(
							"absolute right-1 top-[var(--design-arrow-top)] z-10 flex size-10 -translate-y-1/2 items-center justify-center sm:right-2 sm:size-11",
							mediaCarouselArrowGlass,
							"[&>svg]:size-5 sm:[&>svg]:size-6",
						)}
					/>
				) : null}
			<div
				ref={scrollerRef}
				className={cn(
					"hide-scrollbar flex justify-start gap-3 overflow-x-auto scroll-smooth px-1 pb-1 pt-1",
				)}
			>
				{options.map(({ id, variant, designNumber }) => {
					const selected = selectedDesignId === id;
					const src = firstImageUrl(variant);
					const refKey = id ?? "__landing__";
					const label = selected ? "Current design" : `Design ${designNumber}`;
					return (
						<button
							key={refKey}
							type="button"
							ref={(node) => {
								if (node) itemRefs.current.set(refKey, node);
								else itemRefs.current.delete(refKey);
							}}
							onClick={() => {
								if (selected) return;
								const previous =
									selectedDesignId ?? landingService._id ?? landingService.id ?? "";
								const nextId = id ?? landingService._id ?? landingService.id ?? "";
								ga4SelectDesign({
									item_id: landingService._id || landingService.id || "",
									item_name: landingService.title,
									design_id: nextId,
									design_number: designNumber,
									design_title: variant.title || `Design ${designNumber}`,
									...(previous ? { previous_design_id: previous } : {}),
								});
								onSelect(id);
							}}
							aria-pressed={selected}
							{...getCtaDataAttrs({
								ctaId: `design_picker_design_${designNumber}`,
								ctaName: label,
								ctaLocation: "service_design_picker",
							})}
							className={cn(
								"group w-[15rem] shrink-0 pb-1 text-center sm:w-[17rem]",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2",
							)}
						>
							<span
								ref={designNumber === 1 ? thumbRef : undefined}
								className={cn(
									"relative block aspect-video overflow-hidden rounded-xl bg-muted/30 transition",
									selected
										? "border-[3px] border-tertiary shadow-[0_6px_18px_rgba(23,199,184,0.35)]"
										: "border-2 border-standard shadow-sm dark:shadow-custom",
								)}
							>
								{src ? (
									// eslint-disable-next-line @next/next/no-img-element
									<img
										src={src}
										alt=""
										className="size-full object-cover object-top"
										loading="lazy"
									/>
								) : (
									<span className="flex size-full items-center justify-center text-xs text-muted-foreground">
										No image
									</span>
								)}
							</span>
							<span
								className={cn(
									"mt-2 block px-0.5 text-center text-xs font-medium leading-snug sm:text-sm",
									selected
										? "text-tertiary"
										: "text-grey-foreground dark:text-muted-foreground",
								)}
							>
								{label}
							</span>
							{selected ? (
								<ChevronDown
									className="mx-auto mt-1.5 size-5 shrink-0 text-tertiary"
									aria-hidden
								/>
							) : (
								<span className="mt-1.5 block h-5 shrink-0" aria-hidden />
							)}
						</button>
					);
				})}
			</div>
			</div>
		</section>
	);
}

/** Normalize populated / id-only designVariantServiceIds from the landing service. */
export function resolveDesignVariants(service: Service): ServiceDesignVariant[] {
	const raw = service.designVariantServiceIds ?? [];
	const out: ServiceDesignVariant[] = [];
	for (const entry of raw) {
		if (!entry || typeof entry === "string") continue;
		const id = entry._id || entry.id;
		if (!id || id === service._id) continue;
		out.push({
			_id: id,
			id: entry.id ?? id,
			title: entry.title ?? "Design",
			slug: entry.slug ?? "",
			thumbnailImage: entry.thumbnailImage ?? "",
			thumbnailDisplayUrl: entry.thumbnailDisplayUrl ?? "",
			media: entry.media ?? [],
			detailsHtml: entry.detailsHtml,
			envatoTemplateLicenseAmounts: entry.envatoTemplateLicenseAmounts,
			demoUrl: entry.demoUrl ?? null,
		});
	}
	return out;
}
